import { blockCatalog, getPath, hasPlaceholder, PLACEHOLDER, setPath, type Block, type FieldDef } from '@agency/contracts';
import { Button, IconButton, Notice, SelectField, TextAreaField, TextField } from '@agency/ui';

type Issue = { path: string[]; message: string };
type Obj = Record<string, unknown>;

/** A new empty item for a list field: required text starts as visible placeholder copy so the draft stays valid but cannot be published by accident. */
function blankFor(fields: FieldDef[]): Obj {
  let o: Obj = {};
  for (const f of fields) {
    if (f.kind === 'text' || f.kind === 'textarea') { if (!f.optional) o = setPath(o, f.key, `${PLACEHOLDER}${f.label}`); }
    else if (f.kind === 'href') { if (!f.optional) o = setPath(o, f.key, '/'); }
    else if (f.kind === 'select') { if (!f.optional) o = setPath(o, f.key, f.options[0]!.value); }
    else if (f.kind === 'checkbox') o = setPath(o, f.key, false);
    else if (f.kind === 'strings') o[f.key] = [];
    else if (f.kind === 'list') o[f.key] = Array.from({ length: f.min }, () => blankFor(f.fields));
  }
  return o;
}

interface FieldsProps {
  fields: FieldDef[];
  value: Obj;
  onChange: (next: Obj) => void;
  readOnly: boolean;
  /** Dotted path of `value` inside the block, to match Zod issue paths. */
  prefix: string;
  issues: Issue[];
  /** Used to make repeated labels unique and descriptive ("Title (Benefit 2)"). */
  suffix?: string;
}

function Fields({ fields, value, onChange, readOnly, prefix, issues, suffix }: FieldsProps) {
  const errAt = (key: string) => issues.find((i) => i.path.join('.') === `${prefix}${key}`)?.message;
  return (
    <>
      {fields.map((f) => {
        const label = suffix ? `${f.label} (${suffix})` : f.label;
        const raw = getPath(value, f.key);
        const set = (v: unknown) => onChange(setPath(value, f.key, v));
        switch (f.kind) {
          case 'text':
          case 'href': {
            const s = typeof raw === 'string' ? raw : '';
            const placeholder = s.startsWith(PLACEHOLDER);
            return <TextField key={f.key} label={label} required={!f.optional} value={s} readOnly={readOnly} hint={placeholder ? 'Placeholder text. Replace it before publishing.' : f.kind === 'href' ? (f.hint ?? 'An https:// link, a /path or a #anchor') : f.hint} error={errAt(f.key)} onChange={(e) => set(e.target.value)} />;
          }
          case 'textarea': {
            const s = typeof raw === 'string' ? raw : '';
            const placeholder = s.startsWith(PLACEHOLDER);
            return <TextAreaField key={f.key} label={label} required={!f.optional} rows={3} value={s} readOnly={readOnly} hint={placeholder ? 'Placeholder text. Replace it before publishing.' : f.hint} error={errAt(f.key)} onChange={(e) => set(e.target.value)} />;
          }
          case 'select':
            return (
              <SelectField key={f.key} label={label} value={typeof raw === 'string' ? raw : ''} disabled={readOnly} error={errAt(f.key)} onChange={(e) => set(e.target.value)}>
                {f.optional ? <option value="">Default</option> : null}
                {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </SelectField>
            );
          case 'checkbox':
            return (
              <div key={f.key} className="ag-field">
                <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                  <input type="checkbox" checked={raw === true} disabled={readOnly} onChange={(e) => set(e.target.checked)} /> {label}
                </label>
                {f.hint ? <span className="ag-hint">{f.hint}</span> : null}
              </div>
            );
          case 'number':
            return <TextField key={f.key} label={label} type="number" min={f.min} max={f.max} value={typeof raw === 'number' ? String(raw) : ''} readOnly={readOnly} error={errAt(f.key)} onChange={(e) => set(e.target.value === '' ? undefined : Number(e.target.value))} />;
          case 'strings': {
            const arr = Array.isArray(raw) ? (raw as string[]) : [];
            return (
              <fieldset key={f.key} className="rep-set">
                <legend>{label}</legend>
                {f.hint ? <p className="ag-hint" style={{ margin: 0 }}>{f.hint}</p> : null}
                {arr.map((s, i) => (
                  <div key={i} className="rep-row">
                    <TextField label={`${f.itemLabel} ${i + 1}`} value={s} readOnly={readOnly} error={errAt(`${f.key}.${i}`)} onChange={(e) => set(arr.map((x, k) => (k === i ? e.target.value : x)))} />
                    {!readOnly ? <IconButton icon="x" label={`Remove ${f.itemLabel.toLowerCase()} ${i + 1}`} onClick={() => set(arr.filter((_, k) => k !== i))} /> : null}
                  </div>
                ))}
                {!readOnly ? <div><Button size="sm" icon="plus" aria-disabled={arr.length >= f.max || undefined} onClick={() => { if (arr.length < f.max) set([...arr, '']); }}>Add {f.itemLabel.toLowerCase()}</Button></div> : null}
              </fieldset>
            );
          }
          case 'list': {
            const arr = Array.isArray(raw) ? (raw as Obj[]) : [];
            const move = (i: number, d: -1 | 1) => { const j = i + d; if (j < 0 || j >= arr.length) return; const n = [...arr]; [n[i], n[j]] = [n[j]!, n[i]!]; set(n); };
            return (
              <fieldset key={f.key} className="rep-set">
                <legend>{label}</legend>
                {arr.map((item, i) => (
                  <div className="rep" key={i}>
                    <Fields fields={f.fields} value={item} readOnly={readOnly} prefix={`${prefix}${f.key}.${i}.`} issues={issues} suffix={`${f.itemLabel} ${i + 1}`} onChange={(n) => set(arr.map((x, k) => (k === i ? n : x)))} />
                    {!readOnly ? (
                      <div className="rep__actions">
                        <IconButton icon="chevronUp" label={`Move ${f.itemLabel.toLowerCase()} ${i + 1} up`} aria-disabled={i === 0 || undefined} onClick={() => move(i, -1)} />
                        <IconButton icon="chevronDown" label={`Move ${f.itemLabel.toLowerCase()} ${i + 1} down`} aria-disabled={i === arr.length - 1 || undefined} onClick={() => move(i, 1)} />
                        <Button size="sm" variant="ghost" aria-disabled={arr.length <= f.min || undefined} onClick={() => { if (arr.length > f.min) set(arr.filter((_, k) => k !== i)); }}>Remove {f.itemLabel.toLowerCase()} {i + 1}</Button>
                      </div>
                    ) : null}
                  </div>
                ))}
                {!readOnly ? <div><Button size="sm" icon="plus" aria-disabled={arr.length >= f.max || undefined} onClick={() => { if (arr.length < f.max) set([...arr, blankFor(f.fields)]); }}>Add {f.itemLabel.toLowerCase()}</Button></div> : null}
              </fieldset>
            );
          }
        }
      })}
    </>
  );
}

/** Generic, catalogue-driven inspector: one implementation for every section type. */
export function BlockInspector({ block, index, count, issues, canEdit, onChange, onMove, onRemove }: {
  block: Block; index: number; count: number; issues: Issue[]; canEdit: boolean;
  onChange: (next: Block) => void; onMove: (d: -1 | 1) => void; onRemove: () => void;
}) {
  const entry = blockCatalog[block.type];
  // Issue paths arrive as ['blocks', index, ...field]; the inspector works relative to the block.
  const rel = issues.map((i) => ({ path: i.path.slice(2), message: i.message }));
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <h2 style={{ flex: 1 }}>{entry.label}</h2>
        {canEdit ? (
          <>
            <IconButton icon="chevronUp" label="Move section up" aria-disabled={index === 0 || undefined} onClick={() => index > 0 && onMove(-1)} />
            <IconButton icon="chevronDown" label="Move section down" aria-disabled={index === count - 1 || undefined} onClick={() => index < count - 1 && onMove(1)} />
          </>
        ) : null}
      </div>
      <p className="ag-hint" style={{ margin: 0 }}>{entry.description}</p>
      {hasPlaceholder(block) ? <Notice tone="warning" title="Placeholder copy" live={false}>Fields starting with “{PLACEHOLDER.trim()}” must be replaced before this page can be published.</Notice> : null}
      <Fields fields={entry.fields} value={block as unknown as Obj} readOnly={!canEdit} prefix="" issues={rel} onChange={(n) => onChange(n as unknown as Block)} />
      {canEdit ? <div style={{ borderTop: '1px solid var(--c-divider)', paddingTop: 12 }}><Button variant="danger" size="sm" onClick={onRemove}>Remove section</Button></div> : null}
    </>
  );
}
